import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, IconButton, Button, Tooltip, TableSortLabel, CircularProgress
} from '@mui/material'
import {
  KeyboardArrowLeft, KeyboardArrowRight, Refresh, FileDownload
} from '@mui/icons-material'
import { headingBlue, maroon, mediumGrey, lightGrey, green, orange } from '../../assets/colors'
import { tableOffsets } from '../../utils/constants'
import { buttonStyle } from '../../utils/commonCSS'
import { getStateHeadWorkerCategorization, getAllStates } from '../../backend/backendApis'
import HttpService from '../../backend/requestApis'
import CustomTextField from '../CustomTextField'
import CustomDropDown from '../CustomDropDown'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import _ from 'lodash'

const outerBoxStyle = {
  width: '100%',
  marginTop: "0.2rem"
}

const paperStyle = {
  border: '1px solid #D9D9D9',
  padding: "0.2rem 0.1rem 0.1rem 0.1rem"
}

const headerBoxStyle = {
  display: 'flex',
  padding: "0.3rem",
  flexWrap: 'wrap'
}

const internalBoxStyle = {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '20%',
  flex: '1 1',
  flexDirection: "row",
  flexWrap: 'wrap'
}

const typographyStyle = {
  marginLeft: "0.5rem",
  padding: "0.1rem",
  fontSize: "1rem",
  fontWeight: "600"
}

const headerCellStyle = {
  padding: '0.2rem 0.5rem',
  fontSize: "0.9rem",
  fontWeight: "400",
  textAlign: 'center'
}

const scrollbarStyle = {
  "&::-webkit-scrollbar": { display: 'none' },
  scrollbarWidth: "none"
}

const genderOptions = [
  { name: 'All' },
  { name: 'Male' },
  { name: 'Female' }
]

const StateStatsTable = ({ stateList = [] }) => {
  // Query parameters state
  const [page, setPage] = useState(1)
  const [offset, setOffset] = useState(15)
  const [selectedState, setSelectedState] = useState('All States')
  const [searchText, setSearchText] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [genderFilter, setGenderFilter] = useState('all') // 'all' | 'Male' | 'Female'
  const [sortBy, setSortBy] = useState('unCategorized')
  const [sortOrder, setSortOrder] = useState('desc')

  // API data state
  const [tableData, setTableData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoading, setIsLoading] = useState(false)

  // Fallback states list if not provided
  const [localStateList, setLocalStateList] = useState([])

  useEffect(() => {
    if (!stateList || stateList.length === 0) {
      let options = {
        url: getAllStates,
        method: 'GET',
        headers: {
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
        }
      }
      HttpService(options)
        .then((resp) => {
          let fetched = resp?.data?.data || []
          if (Array.isArray(fetched)) {
            let formatted = fetched.map(item => (typeof item === 'string' ? { name: item } : { name: item.name || item.state || item.stateName || item }))
            setLocalStateList([{ name: 'All States' }, ...formatted])
          }
        })
        .catch(() => null)
    }
  }, [stateList])

  const stateDropdownOptions = useMemo(() => {
    const src = stateList && stateList.length > 0 ? stateList : localStateList
    const list = src.map(item => (typeof item === 'string' ? { name: item } : { name: item.name }))
    if (!list.some(s => s.name === 'All States')) {
      return [{ name: 'All States' }, ...list]
    }
    return list
  }, [stateList, localStateList])

  // Debounced search
  const debouncedSearch = useCallback(
    _.debounce((val) => {
      setSearchText(val)
      setPage(1)
    }, 450),
    []
  )

  const handleSearchChange = (val) => {
    setSearchInput(val)
    debouncedSearch(val)
  }

  // Fetch API data
  const fetchData = useCallback(() => {
    setIsLoading(true)
    const stateParam = selectedState === 'All States' ? 'all' : selectedState

    const params = {
      page,
      offset,
      state: stateParam || 'all',
      sortBy,
      sortOrder
    }

    if (searchText && searchText.trim() !== '') {
      params.searchText = searchText.trim()
    }

    if (genderFilter && genderFilter !== 'all') {
      params.gender = genderFilter
    }

    const options = {
      url: getStateHeadWorkerCategorization,
      method: 'GET',
      params,
      headers: {
        Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
      }
    }

    HttpService(options)
      .then((resp) => {
        const payload = resp?.data?.data
        if (payload) {
          setTableData(payload.data || [])
          setTotalRecords(payload.totalRecords || 0)
          setTotalPages(payload.totalPages || 1)
        } else {
          setTableData([])
          setTotalRecords(0)
          setTotalPages(1)
        }
        setIsLoading(false)
      })
      .catch((err) => {
        console.log('Error fetching State Head WORKer Categorization data: ', err)
        setTableData([])
        setTotalRecords(0)
        setTotalPages(1)
        setIsLoading(false)
      })
  }, [page, offset, selectedState, searchText, genderFilter, sortBy, sortOrder])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Sort handler
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
    setPage(1)
  }

  const [isDownloading, setIsDownloading] = useState(false)

  const generateExcelFile = (records) => {
    const transformedData = records.map((row, index) => {
      const rowTotal = Number(row.totalWORKers || 0)
      const committed = Number(row.committed || 0)
      const unCommitted = Number(row.unCommitted || 0)
      const unCategorized = Number(row.unCategorized || 0)
      const committedPct = rowTotal > 0 ? `${Math.round((committed / rowTotal) * 100)}%` : '0%'
      const uncommittedPct = rowTotal > 0 ? `${Math.round((unCommitted / rowTotal) * 100)}%` : '0%'
      const uncategorizedPct = rowTotal > 0 ? `${Math.round((unCategorized / rowTotal) * 100)}%` : '0%'
      const distList = Array.isArray(row.districts) ? row.districts.join(', ') : (row.districts || '')

      return {
        'S.No': index + 1,
        'State Head': row.headName || '-',
        'Contact Number': row.headCN || '-',
        'State / Chapter': row.name || '-',
        'Gender': row.gender || '-',
        'Assigned Districts': distList || '-',
        'Total WORKers': rowTotal,
        'Committed WORKers': committed,
        'Committed WORKers (%)': committedPct,
        'Supported WORKer': unCommitted,
        'Supported WORKer (%)': uncommittedPct,
        'Uncategorized': unCategorized,
        'Uncategorized (%)': uncategorizedPct
      }
    })

    const worksheet = XLSX.utils.json_to_sheet(transformedData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'State Categorization')
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    })
    saveAs(blob, 'States Committed WORKers Supported WORKer data.xlsx')
  }

  const handleDownloadExcel = async () => {
    if (isDownloading) return

    if (totalRecords > 0 && tableData.length >= totalRecords) {
      generateExcelFile(tableData)
      return
    }

    try {
      setIsDownloading(true)
      const stateParam = selectedState === 'All States' ? 'all' : selectedState

      const params = {
        page: 1,
        offset: totalRecords > 0 ? totalRecords : 10000,
        state: stateParam || 'all',
        sortBy,
        sortOrder
      }

      if (searchText && searchText.trim() !== '') {
        params.searchText = searchText.trim()
      }

      if (genderFilter && genderFilter !== 'all') {
        params.gender = genderFilter
      }

      const options = {
        url: getStateHeadWorkerCategorization,
        method: 'GET',
        params,
        headers: {
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
        }
      }

      const resp = await HttpService(options)
      const records = resp?.data?.data?.data || []
      if (records.length > 0) {
        generateExcelFile(records)
      } else {
        generateExcelFile(tableData)
      }
    } catch (err) {
      console.log('Error downloading Excel: ', err)
      if (tableData && tableData.length > 0) {
        generateExcelFile(tableData)
      }
    } finally {
      setIsDownloading(false)
    }
  }

  const handleResetFilters = () => {
    setSelectedState('All States')
    setSearchText('')
    setSearchInput('')
    setGenderFilter('all')
    setSortBy('unCategorized')
    setSortOrder('desc')
    setPage(1)
  }

  return (
    <Box style={outerBoxStyle}>
      <Paper style={paperStyle}>
        {/* Top Action Bar */}
        <Box style={headerBoxStyle}>
          <Box style={internalBoxStyle}>
            <Typography style={typographyStyle}>State Head WORKer Categorization</Typography>
          </Box>
          <Box style={{ display: 'flex', flex: "1 1", width: "80%", justifyContent: 'flex-end', padding: "0 0.5rem", flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
            <CustomDropDown
              key="stateCategorizationFilter"
              width="12rem"
              list={stateDropdownOptions}
              value={selectedState}
              placeHolder="All States"
              setFunc={(val) => {
                setSelectedState(val)
                setPage(1)
              }}
              regular={true}
            />

            <CustomDropDown
              key="genderCategorizationFilter"
              width="6.5rem"
              list={genderOptions}
              value={genderFilter === 'all' ? 'All' : genderFilter}
              placeHolder="All"
              setFunc={(val) => {
                setGenderFilter(val === 'All' ? 'all' : val)
                setPage(1)
              }}
              regular={true}
            />

            <CustomTextField
              placeHolder="Search head, contact, district..."
              value={searchInput}
              setFunc={handleSearchChange}
            />

            <Tooltip title="Reset filters">
              <IconButton size="small" onClick={handleResetFilters} sx={{ color: headingBlue }}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              onClick={handleDownloadExcel}
              disabled={isDownloading || tableData.length === 0}
              sx={{
                ...buttonStyle,
                textTransform: 'none',
                height: '33px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                margin: 0,
                fontSize: '0.82rem',
                fontWeight: 600,
                '&:hover': { backgroundColor: headingBlue }
              }}
            >
              {isDownloading ? (
                <>
                  <CircularProgress size={14} sx={{ color: 'white', mr: 0.5 }} />
                  Downloading...
                </>
              ) : (
                <>
                  <FileDownload fontSize="small" />
                  Download
                </>
              )}
            </Button>
          </Box>
        </Box>

        {/* Table Area */}
        <Box style={{ display: 'flex', justifyContent: 'center', overflow: 'auto' }}>
          <TableContainer component={Paper} sx={{ width: '98%', height: "70vh", border: '1px solid #D9D9D9', mt: 0.2, ...scrollbarStyle }}>
            <Table stickyHeader>
              <TableHead style={{ backgroundColor: mediumGrey }}>
                <TableRow>
                  <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey, width: '45px' }}>
                    S.No
                  </TableCell>

                  <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey, textAlign: 'left' }}>
                    <TableSortLabel
                      active={sortBy === 'headName'}
                      direction={sortBy === 'headName' ? sortOrder : 'desc'}
                      onClick={() => handleSort('headName')}
                      sx={{ color: `${headingBlue} !important` }}
                    >
                      State Head
                    </TableSortLabel>
                  </TableCell>

                  <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey, textAlign: 'left' }}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortOrder : 'desc'}
                      onClick={() => handleSort('name')}
                      sx={{ color: `${headingBlue} !important` }}
                    >
                      State / Chapter
                    </TableSortLabel>
                  </TableCell>

                  <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey, width: '75px' }}>
                    Gender
                  </TableCell>

                  <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey, minWidth: '150px' }}>
                    Assigned Districts
                  </TableCell>

                  <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey }}>
                    <TableSortLabel
                      active={sortBy === 'totalWORKers'}
                      direction={sortBy === 'totalWORKers' ? sortOrder : 'desc'}
                      onClick={() => handleSort('totalWORKers')}
                      sx={{ color: `${headingBlue} !important` }}
                    >
                      Total WORKers
                    </TableSortLabel>
                  </TableCell>

                  <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey, color: green }}>
                    <TableSortLabel
                      active={sortBy === 'committed'}
                      direction={sortBy === 'committed' ? sortOrder : 'desc'}
                      onClick={() => handleSort('committed')}
                      sx={{ color: `${headingBlue} !important` }}
                    >
                      Committed WORKers
                    </TableSortLabel>
                  </TableCell>

                  <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey, color: orange }}>
                    <TableSortLabel
                      active={sortBy === 'unCommitted'}
                      direction={sortBy === 'unCommitted' ? sortOrder : 'desc'}
                      onClick={() => handleSort('unCommitted')}
                      sx={{ color: `${headingBlue} !important` }}
                    >
                      Supported WORKer
                    </TableSortLabel>
                  </TableCell>

                  <TableCell style={{ ...headerCellStyle, backgroundColor: lightGrey, color: maroon }}>
                    <TableSortLabel
                      active={sortBy === 'unCategorized'}
                      direction={sortBy === 'unCategorized' ? sortOrder : 'desc'}
                      onClick={() => handleSort('unCategorized')}
                      sx={{ color: `${headingBlue} !important` }}
                    >
                      Uncategorized
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody style={{ textAlign: 'center' }}>
                {tableData && tableData.length > 0 ? (
                  tableData.map((row, index) => {
                    const rowTotal = Number(row.totalWORKers || 0)
                    const committedPct = rowTotal > 0 ? Math.round((Number(row.committed || 0) / rowTotal) * 100) : 0
                    const uncommittedPct = rowTotal > 0 ? Math.round((Number(row.unCommitted || 0) / rowTotal) * 100) : 0
                    const uncategorizedPct = rowTotal > 0 ? Math.round((Number(row.unCategorized || 0) / rowTotal) * 100) : 0
                    const distList = Array.isArray(row.districts) ? row.districts : []

                    return (
                      <TableRow key={row._id || index} hover>
                        {/* S.No */}
                        <TableCell style={headerCellStyle}>
                          {(page - 1) * offset + index + 1}
                        </TableCell>

                        {/* State Head */}
                        <TableCell style={{ ...headerCellStyle, textAlign: 'left' }}>
                          <Typography variant="body2" style={{ fontWeight: 'bold', color: '#222222', fontSize: '0.9rem' }}>
                            {row.headName || '-'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {row.headCN || '-'}
                          </Typography>
                        </TableCell>

                        {/* State / Chapter */}
                        <TableCell style={{ ...headerCellStyle, textAlign: 'left' }}>
                          <Typography variant="body2">
                            {row.name || '-'}
                          </Typography>
                        </TableCell>

                        {/* Gender */}
                        <TableCell style={headerCellStyle}>
                          <Typography variant="body2">
                            {row.gender || '-'}
                          </Typography>
                        </TableCell>

                        {/* Assigned Districts */}
                        <TableCell style={headerCellStyle}>
                          {distList.length === 0 ? (
                            '-'
                          ) : distList.length <= 2 ? (
                            <Tooltip title={distList.join(', ')} arrow>
                              <Typography variant="body2" style={{ cursor: 'pointer' }}>
                                {distList.join(', ')}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Tooltip title={distList.join(', ')} arrow placement="top">
                              <Typography variant="body2" style={{ cursor: 'pointer' }}>
                                {distList[0]} (+{distList.length - 1} more)
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>

                        {/* Total WORKers */}
                        <TableCell style={headerCellStyle}>
                          <Typography variant="body2" style={{ fontWeight: 'bold', color: headingBlue }}>
                            {row.totalWORKers ?? 0}
                          </Typography>
                        </TableCell>

                        {/* Committed */}
                        <TableCell style={{ ...headerCellStyle, color: green }}>
                          <Typography variant="body2" style={{ fontWeight: 500, color: green }}>
                            {row.committed ?? 0} {rowTotal > 0 && `(${committedPct}%)`}
                          </Typography>
                        </TableCell>

                        {/* Uncommitted */}
                        <TableCell style={{ ...headerCellStyle, color: orange }}>
                          <Typography variant="body2" style={{ fontWeight: 500, color: orange }}>
                            {row.unCommitted ?? 0} {rowTotal > 0 && `(${uncommittedPct}%)`}
                          </Typography>
                        </TableCell>

                        {/* Uncategorized */}
                        <TableCell style={{ ...headerCellStyle, color: Number(row.unCategorized || 0) > 0 ? maroon : 'inherit' }}>
                          <Typography variant="body2" style={{ fontWeight: 500, color: Number(row.unCategorized || 0) > 0 ? maroon : 'inherit' }}>
                            {row.unCategorized ?? 0} {rowTotal > 0 && Number(row.unCategorized || 0) > 0 && `(${uncategorizedPct}%)`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} style={{ ...headerCellStyle, padding: '1rem', color: maroon }}>
                      {isLoading ? 'Loading State Head WORKer Categorization details...' : 'No Records Found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination Area */}
        <Box style={{ display: 'flex', justifyContent: 'center', padding: "0.4rem 0rem 0.1rem 0rem" }}>
          {/* 1. Record Count (Left) */}
          <Box style={{ width: "30%", display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: "0rem 0.5rem 0rem 0.9rem" }}>
            <Typography style={{ color: maroon, fontSize: '0.9rem' }}>
              {totalRecords > 0
                ? `${((page - 1) * offset) + 1} - ${Math.min(page * offset, totalRecords)} of ${totalRecords}`
                : '0 - 0 of 0'}
            </Typography>
          </Box>

          {/* 2. Page Controls (Center) */}
          <Box style={{ width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <IconButton
              size='small'
              onClick={() => { if (page > 1) setPage(page - 1) }}
              disabled={page === 1 || isLoading}
            >
              <KeyboardArrowLeft size='small' sx={{ color: page === 1 ? mediumGrey : headingBlue }} />
            </IconButton>

            <Button
              variant='contained'
              style={{
                minWidth: '0rem',
                width: '1.5rem',
                height: '1.5rem',
                borderRadius: '50%',
                backgroundColor: headingBlue,
                color: 'white',
                padding: '0rem',
                fontSize: '0.85rem'
              }}
            >
              {page}
            </Button>

            <IconButton
              size='small'
              onClick={() => { if (page < totalPages) setPage(page + 1) }}
              disabled={page === totalPages || !totalPages || isLoading}
            >
              <KeyboardArrowRight size='small' sx={{ color: (page === totalPages || !totalPages || isLoading) ? mediumGrey : headingBlue }} />
            </IconButton>
          </Box>

          {/* 3. Offset Dropdown (Right) */}
          <Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-end', padding: "0rem 0.6rem 0rem 0rem" }}>
            <CustomDropDown
              key='stateCategorizationOffset'
              value={offset}
              width="5rem"
              setFunc={(newOffset) => {
                setPage(1)
                setOffset(newOffset)
              }}
              list={tableOffsets}
              regular={true}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default StateStatsTable
